/**
 * Labels for the Inspect Assessment picker.
 *
 * The three selects at the top of the page are the first thing an admin meets,
 * so every row says how much data sits behind it: an assessment that nobody
 * has played, a klass where only two candidates were graded, a candidate with
 * one unscored attempt. These helpers are pure so the wording can be tested
 * without a store; the page turns the returned strings into nodes.
 *
 * Every count arrives from the API as an optional field. When a count is
 * missing the row says nothing about it rather than guessing a zero.
 */
import type {
  InspectAssessmentKlass,
  InspectAssessmentOption,
  InspectAssessmentUser,
} from '../../../../state/app/inspect_assessment/types';

export type InspectAssessmentPickerOption = {
  /** The option id, the Select value. */
  value: number;
  /** The first line, and what the closed Select shows. */
  label: string;
  /** The quiet second line in the dropdown. Empty when there is nothing to say. */
  detail: string;
  /** Everything typing can match, including fields not shown on the row. */
  searchText: string;
  /** The full row as one line, for the hover title. */
  tooltip: string;
};

/** One antd option group: a heading with its own rows underneath. */
export type InspectAssessmentPickerGroup = {
  /** The heading, already carrying the count of its members. */
  label: string;
  options: InspectAssessmentPickerOption[];
};

/**
 * AssessmentScenario.ACTIVE. A key at this status is in service: a class
 * could be sat on it tomorrow, which is why the server keeps it in the
 * default list even when nobody has played it yet.
 */
export const ASSESSMENT_STATUS_ACTIVE = 1;

const countLabel = (count: number, singular: string, plural?: string) =>
  `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;

const joinParts = (parts: (string | undefined)[]) =>
  parts.filter((part): part is string => Boolean(part)).join(' · ');

/**
 * An assessment is worth offering when somebody has played it OR it is in
 * service. This MIRRORS the server's own default filter, and it has to: the
 * "N with data" summary counts rows with this predicate, so a predicate that
 * disagrees with the server would announce a filter nobody applied.
 *
 * Status alone must never decide. On the live fleet 37 of the 38 keys anyone
 * has ever sat are RETIRED, superseded by a regeneration, and those are the
 * main thing an admin comes to this page to inspect.
 *
 * An older server sends no session_count, and then every row is offered.
 */
export const assessmentHasData = (assessment: InspectAssessmentOption) =>
  assessment.session_count === undefined ||
  assessment.session_count > 0 ||
  assessment.status === ASSESSMENT_STATUS_ACTIVE;

/** A klass is worth picking when somebody in it has actually played. */
export const klassHasData = (klass: InspectAssessmentKlass) =>
  (klass.session_count ?? 1) > 0;

/** A candidate is worth picking when they have an attempt on record. */
export const userHasData = (user: InspectAssessmentUser) =>
  (user.attempt_count ?? 1) > 0;

export const formatComposite = (composite?: number | null) =>
  composite === null || composite === undefined || !Number.isFinite(composite)
    ? undefined
    : composite.toFixed(2);

/** How much of the exercise there is: periods and instruments. */
const describeAssessmentSize = (assessment: InspectAssessmentOption) =>
  assessment.period_count === undefined
    ? undefined
    : joinParts([
        countLabel(assessment.period_count, 'period'),
        assessment.instrument_count === undefined
          ? undefined
          : countLabel(assessment.instrument_count, 'instrument'),
      ]);

/** How much it has been used: sittings, grades, klasses. */
const describeAssessmentUsage = (assessment: InspectAssessmentOption) => {
  if (assessment.session_count === undefined) return undefined;
  if (assessment.session_count === 0)
    return 'exercise only — nobody has played it yet';
  return joinParts([
    countLabel(assessment.session_count, 'session'),
    // graded_count counts graded SITTINGS here — one result per link and
    // player — so it is named as sittings. Read as candidates it would
    // outrun the roster the moment anybody resat the key.
    assessment.graded_count === undefined
      ? undefined
      : countLabel(assessment.graded_count, 'graded sitting'),
    assessment.klass_count === undefined
      ? undefined
      : countLabel(assessment.klass_count, 'klass', 'klasses'),
  ]);
};

export const buildAssessmentPickerOption = (
  assessment: InspectAssessmentOption,
): InspectAssessmentPickerOption => {
  const label = joinParts([
    assessment.name,
    assessment.code,
    assessment.status_label,
  ]);
  const detail = joinParts([
    describeAssessmentUsage(assessment),
    describeAssessmentSize(assessment),
  ]);
  const searchText = joinParts([
    assessment.name,
    assessment.code,
    assessment.model_version,
    assessment.status_label,
  ]);
  return {
    value: assessment.id,
    label,
    detail,
    searchText,
    tooltip: joinParts([searchText, detail]),
  };
};

export const buildAssessmentPickerOptions = (
  assessments: InspectAssessmentOption[],
): InspectAssessmentPickerOption[] =>
  assessments.map(buildAssessmentPickerOption);

/**
 * The assessment list, split into the three kinds of row it holds.
 *
 * A flat list of every answer key is the complaint this page opened with:
 * on production the default list is ~208 keys, of which only ~38 were ever
 * sat and ~170 are simply in service. The split says which is which before
 * anything is clicked:
 *
 *   Played                        session_count > 0 — the evidence is here
 *   In service, not played yet    ACTIVE, nobody has sat it
 *   Never played, out of service  retired or draft, only under Show all
 *
 * A group with no members is left out, so the third heading appears only
 * when Show all put those rows back. Returns undefined when the server sent
 * no session counts — an older server cannot be sorted into these groups,
 * and the caller then renders one ungrouped list as before.
 */
export const buildAssessmentPickerGroups = (
  assessments: InspectAssessmentOption[],
): InspectAssessmentPickerGroup[] | undefined => {
  if (assessments.length === 0) return undefined;
  if (assessments.some(assessment => assessment.session_count === undefined))
    return undefined;

  const played: InspectAssessmentOption[] = [];
  const inService: InspectAssessmentOption[] = [];
  const outOfService: InspectAssessmentOption[] = [];
  assessments.forEach(assessment => {
    if ((assessment.session_count ?? 0) > 0) played.push(assessment);
    else if (assessment.status === ASSESSMENT_STATUS_ACTIVE)
      inService.push(assessment);
    else outOfService.push(assessment);
  });

  return [
    { label: 'Played', members: played },
    { label: 'In service, not played yet', members: inService },
    { label: 'Never played, out of service', members: outOfService },
  ]
    .filter(group => group.members.length > 0)
    .map(group => ({
      label: `${group.label} (${group.members.length})`,
      options: group.members.map(buildAssessmentPickerOption),
    }));
};

/**
 * "N of M candidates graded" counts PEOPLE on both sides of the "of":
 * graded_user_count, the distinct candidates with a mark, against
 * user_count, the roster. graded_count is a different unit — graded
 * sittings — and reading it here let a resit report 45 of 15. A server
 * that does not send graded_user_count gets silence on that half rather
 * than the wrong number, and N is clamped so it can never outrun M.
 */
export const describeKlassUsage = (klass: InspectAssessmentKlass) => {
  if (klass.user_count === undefined && klass.session_count === undefined)
    return '';
  if (klass.session_count === 0) return 'nobody has played it here yet';
  // A sitting whose player has since left the roster: the class is offered
  // on the strength of the sitting, so saying "nobody has played it here"
  // would contradict the very number that kept it in the list.
  if (klass.user_count === 0)
    return joinParts([
      klass.session_count === undefined
        ? undefined
        : countLabel(klass.session_count, 'session'),
      'nobody on today’s roster',
    ]);
  const roster = klass.user_count;
  const gradedCandidates = klass.graded_user_count;
  return joinParts([
    roster === undefined || gradedCandidates === undefined
      ? undefined
      : `${Math.min(gradedCandidates, roster)} of ${roster} candidates graded`,
    klass.session_count === undefined
      ? undefined
      : countLabel(klass.session_count, 'session'),
  ]);
};

export const buildKlassPickerOption = (
  klass: InspectAssessmentKlass,
): InspectAssessmentPickerOption => {
  const detail = describeKlassUsage(klass);
  return {
    value: klass.id,
    label: klass.name,
    detail,
    searchText: klass.name,
    tooltip: joinParts([klass.name, detail]),
  };
};

export const buildKlassPickerOptions = (
  klasses: InspectAssessmentKlass[],
): InspectAssessmentPickerOption[] => klasses.map(buildKlassPickerOption);

/** The badge under a name in the USERS sidecar. */
export const describeUserActivity = (user: InspectAssessmentUser) => {
  if (user.attempt_count === undefined) return '';
  if (user.attempt_count === 0) return 'no attempt yet';
  const attempts = countLabel(user.attempt_count, 'attempt');
  const composite = formatComposite(user.last_composite);
  if (!composite) return `${attempts} · not scored yet`;
  return joinParts([
    attempts,
    user.graded_count === undefined || user.graded_count <= 1
      ? undefined
      : `${user.graded_count} graded`,
    `last score ${composite}`,
  ]);
};

export type PickerFilterSummary = {
  /** What one row is, in plain words: 'assessment', 'klass', 'candidate'. */
  noun: string;
  nounPlural: string;
  /** Rows the list currently holds. */
  shown: number;
  /** Of those, how many have data behind them. */
  withData: number;
  /**
   * Whether the server actually narrowed THIS list.
   *
   * It is not the same question as "is Show all off". The users endpoint
   * only filters when an assessment is sent with the request, so in the
   * page's default state the whole roster comes back with Show all off,
   * and a summary that assumed the filter had run announced every row as
   * having data. The caller knows which of its lists were narrowed; it has
   * to say so, because the count alone cannot tell.
   */
  serverFiltered: boolean;
  loading?: boolean;
  failed?: boolean;
  /** Rows in the unfiltered list, when it has been seen at least once. */
  knownTotal?: number;
};

/**
 * The quiet line under a select that explains what the list is holding back.
 * Silent on failure: the error alert is already saying it.
 */
export const describePickerFilter = ({
  noun,
  nounPlural,
  shown,
  withData,
  serverFiltered,
  loading,
  failed,
  knownTotal,
}: PickerFilterSummary) => {
  if (failed) return '';
  if (loading) return 'Loading…';
  if (!serverFiltered) {
    // Nothing was held back, so nothing may be claimed: say what the list
    // holds and how much of it is worth opening.
    const empty = shown - withData;
    return joinParts([
      countLabel(shown, noun, nounPlural),
      empty > 0 ? `${withData} with data · ${empty} empty` : undefined,
    ]);
  }
  if (shown === 0)
    return `No ${noun} has data yet. Switch on Show all to see every ${noun}.`;
  const hidden =
    knownTotal === undefined ? undefined : Math.max(0, knownTotal - shown);
  return joinParts([
    `${shown} with data`,
    hidden === undefined
      ? `empty ${nounPlural} hidden`
      : hidden > 0
        ? `${hidden} empty hidden`
        : undefined,
  ]);
};
